import { useState, useEffect } from 'react';

interface CalculationInputs {
  monthlySavings: number;
  apartmentCost: number;
  apartmentArea: number;
  downPaymentPercent: number;
  renovationCostPerSqm: number;
  deliveryPeriodMonths: number;
  currentSavings: number;
}

interface CalculationResults {
  downPayment: number;
  totalRenovationCost: number;
  savingsDuringConstruction: number;
  totalNeededAtDeal: number;
  stillToSave: number;
  monthsToSave: number;
  yearsToSave: number;
}

const STORAGE_KEY = 'homeplan-calculator-data';

const getInitialInputs = (): CalculationInputs => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.error('Error parsing stored data:', error);
    }
  }
  
  return {
    monthlySavings: 0,
    apartmentCost: 0,
    apartmentArea: 0,
    downPaymentPercent: 0,
    renovationCostPerSqm: 0,
    deliveryPeriodMonths: 0,
    currentSavings: 0,
  };
};

function App() {
  const [inputs, setInputs] = useState<CalculationInputs>(getInitialInputs);

  const [results, setResults] = useState<CalculationResults | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof CalculationInputs, string>>>({});

  // Сохраняем данные в localStorage при изменении inputs
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
  }, [inputs]);

  const validateInputs = (): boolean => {
    const newErrors: Partial<Record<keyof CalculationInputs, string>> = {};

    if (inputs.monthlySavings <= 0) {
      newErrors.monthlySavings = 'Месячная сумма должна быть больше 0';
    }
    if (inputs.apartmentCost <= 0) {
      newErrors.apartmentCost = 'Стоимость квартиры должна быть больше 0';
    }
    if (inputs.apartmentArea <= 0) {
      newErrors.apartmentArea = 'Площадь должна быть больше 0';
    }
    if (inputs.downPaymentPercent <= 0 || inputs.downPaymentPercent > 100) {
      newErrors.downPaymentPercent = 'Процент взноса должен быть от 1 до 100';
    }
    if (inputs.renovationCostPerSqm < 0) {
      newErrors.renovationCostPerSqm = 'Стоимость ремонта не может быть отрицательной';
    }
    if (inputs.deliveryPeriodMonths < 0) {
      newErrors.deliveryPeriodMonths = 'Срок не может быть отрицательным';
    }
    if (inputs.currentSavings < 0) {
      newErrors.currentSavings = 'Накопления не могут быть отрицательными';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatNumberForDisplay = (value: number): string => {
    if (value === 0) return '';
    return new Intl.NumberFormat('pl-PL', {
      useGrouping: true,
    }).format(value);
  };

  const parseNumberFromInput = (value: string): number => {
    // Убираем все пробелы и заменяем запятые на точки
    const cleanValue = value.replace(/\s/g, '').replace(',', '.');
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleInputChange = (field: keyof CalculationInputs, value: string) => {
    const numericValue = parseNumberFromInput(value);
    setInputs(prev => ({
      ...prev,
      [field]: numericValue
    }));
    
    // Очищаем ошибку для этого поля при изменении
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const calculateSavings = () => {
    if (!validateInputs()) {
      return;
    }

    const downPayment = (inputs.apartmentCost * inputs.downPaymentPercent) / 100;
    const totalRenovationCost = inputs.apartmentArea * inputs.renovationCostPerSqm;
    const savingsDuringConstruction = inputs.monthlySavings * inputs.deliveryPeriodMonths;
    // К моменту сделки нужен первоначальный взнос + та часть ремонта, которую не покроют накопления во время строительства
    const renovationNotCoveredByConstruction = Math.max(0, totalRenovationCost - savingsDuringConstruction);
    const totalNeededAtDeal = downPayment + renovationNotCoveredByConstruction;
    const stillToSave = Math.max(0, totalNeededAtDeal - inputs.currentSavings);
    const monthsToSave = stillToSave / inputs.monthlySavings;
    const yearsToSave = monthsToSave / 12;

    setResults({
      downPayment,
      totalRenovationCost,
      savingsDuringConstruction,
      totalNeededAtDeal,
      stillToSave,
      monthsToSave,
      yearsToSave,
    });
  };

  const clearData = () => {
    const emptyInputs = {
      monthlySavings: 0,
      apartmentCost: 0,
      apartmentArea: 0,
      downPaymentPercent: 0,
      renovationCostPerSqm: 0,
      deliveryPeriodMonths: 0,
      currentSavings: 0,
    };
    setInputs(emptyInputs);
    setResults(null);
    setErrors({});
    localStorage.removeItem(STORAGE_KEY);
  };

  const formatNumber = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatTime = (years: number) => {
    const wholeYears = Math.floor(years);
    const months = Math.ceil((years - wholeYears) * 12);
    
    if (wholeYears === 0) {
      return `${months} мес.`;
    } else if (months === 0) {
      return `${wholeYears} г.`;
    } else {
      return `${wholeYears} г. ${months} мес.`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">
          Калькулятор накоплений на ипотеку
        </h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Форма ввода */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-800">Введите данные</h2>
              <div className="text-xs text-gray-500 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Автосохранение
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Месячная сумма накоплений <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.monthlySavings)}
                  onChange={(e) => handleInputChange('monthlySavings', e.target.value)}
                  placeholder="Например: 5 000"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.monthlySavings 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.monthlySavings && (
                  <p className="text-red-500 text-sm mt-1">{errors.monthlySavings}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Стоимость квартиры <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.apartmentCost)}
                  onChange={(e) => handleInputChange('apartmentCost', e.target.value)}
                  placeholder="Например: 500 000"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.apartmentCost 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.apartmentCost && (
                  <p className="text-red-500 text-sm mt-1">{errors.apartmentCost}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Площадь квартиры (м²) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.apartmentArea)}
                  onChange={(e) => handleInputChange('apartmentArea', e.target.value)}
                  placeholder="Например: 50"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.apartmentArea 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.apartmentArea && (
                  <p className="text-red-500 text-sm mt-1">{errors.apartmentArea}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Первоначальный взнос (%) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.downPaymentPercent)}
                  onChange={(e) => handleInputChange('downPaymentPercent', e.target.value)}
                  placeholder="Например: 20"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.downPaymentPercent 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.downPaymentPercent && (
                  <p className="text-red-500 text-sm mt-1">{errors.downPaymentPercent}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Стоимость ремонта за м²
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.renovationCostPerSqm)}
                  onChange={(e) => handleInputChange('renovationCostPerSqm', e.target.value)}
                  placeholder="Например: 4 000"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.renovationCostPerSqm 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.renovationCostPerSqm && (
                  <p className="text-red-500 text-sm mt-1">{errors.renovationCostPerSqm}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Срок между сделкой и сдачей (месяцев)
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.deliveryPeriodMonths)}
                  onChange={(e) => handleInputChange('deliveryPeriodMonths', e.target.value)}
                  placeholder="Например: 18"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.deliveryPeriodMonths 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.deliveryPeriodMonths && (
                  <p className="text-red-500 text-sm mt-1">{errors.deliveryPeriodMonths}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Уже накоплено
                </label>
                <input
                  type="text"
                  value={formatNumberForDisplay(inputs.currentSavings)}
                  onChange={(e) => handleInputChange('currentSavings', e.target.value)}
                  placeholder="Например: 50 000"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.currentSavings 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.currentSavings && (
                  <p className="text-red-500 text-sm mt-1">{errors.currentSavings}</p>
                )}
              </div>

              <div className="space-y-3">
                <button
                  onClick={calculateSavings}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Рассчитать
                </button>
                <button
                  onClick={clearData}
                  className="w-full bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 transition-colors font-medium"
                >
                  Очистить данные
                </button>
              </div>
            </div>
          </div>

          {/* Результаты */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Результаты расчета</h2>
            
            {results ? (
              <div className="space-y-4">
                <div className="border-b pb-3">
                  <div className="text-sm text-gray-600">Первоначальный взнос</div>
                  <div className="text-lg font-semibold text-gray-800">
                    {formatNumber(results.downPayment)}
                  </div>
                </div>

                <div className="border-b pb-3">
                  <div className="text-sm text-gray-600">Стоимость ремонта</div>
                  <div className="text-lg font-semibold text-gray-800">
                    {formatNumber(results.totalRenovationCost)}
                  </div>
                </div>

                <div className="border-b pb-3">
                  <div className="text-sm text-gray-600">Накопления за время строительства</div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatNumber(results.savingsDuringConstruction)}
                  </div>
                  {results.savingsDuringConstruction > results.totalRenovationCost && results.totalRenovationCost > 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      <div>
                        Из этой суммы {formatNumber(results.totalRenovationCost)} пойдет на ремонт, 
                        остальные {formatNumber(results.savingsDuringConstruction - results.totalRenovationCost)} останутся у вас
                      </div>
                      <div className="text-blue-600 mt-1">
                        💡 Можно рассмотреть уменьшение срока между сделкой и сдачей для более быстрого заселения
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="text-sm text-gray-600">Нужно к моменту сделки</div>
                  <div className="text-lg font-semibold text-gray-800">
                    {formatNumber(results.totalNeededAtDeal)}
                  </div>
                </div>

                <div className="border-b pb-3">
                  <div className="text-sm text-gray-600">Осталось накопить</div>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatNumber(results.stillToSave)}
                  </div>
                </div>

                <div className={`p-4 rounded-lg ${results.stillToSave <= 0 ? 'bg-green-50' : 'bg-blue-50'}`}>
                  <div className="text-sm text-gray-600 mb-1">Время до готовности к сделке</div>
                  {results.stillToSave <= 0 ? (
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        Готово! 🎉
                      </div>
                      <div className="text-sm text-gray-500">
                        У вас уже достаточно накоплений для сделки
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-2xl font-bold text-blue-600">
                        {formatTime(results.yearsToSave)}
                      </div>
                      <div className="text-sm text-gray-500">
                        ({Math.ceil(results.monthsToSave)} месяцев)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                Введите данные и нажмите "Рассчитать" для получения результатов
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;